import {
  Fragment,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import type { SessionViewerResult } from '../auth/api';
import { NavigationHistory, navigationScope, type NavigationSnapshot } from './navigation-history';

interface NavigationContext {
  readonly scope: string;
  readonly history: NavigationHistory;
}
const Navigation = createContext<NavigationContext | null>(null);
const emptySnapshot: NavigationSnapshot = { sections: {}, visit: 0 };
const emptySubscribe = (): (() => void) => () => undefined;
const getEmptySnapshot = (): NavigationSnapshot => emptySnapshot;

function NavigationSession({
  scope,
  children,
}: {
  readonly scope: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  const [context] = useState(() => ({ scope, history: new NavigationHistory(scope) }));
  const snapshot = useSyncExternalStore(
    context.history.subscribe,
    context.history.getSnapshot,
    getEmptySnapshot,
  );
  useEffect(() => {
    // Child effects consume OAuth return markers before this parent effect.
    context.history.synchronizeLocation();
    window.addEventListener('popstate', context.history.restore);
    return () => window.removeEventListener('popstate', context.history.restore);
  }, [context]);
  return (
    <Navigation.Provider value={context}>
      {/* A browser visit drops dialogs, selections and drafts instead of restoring business data. */}
      <Fragment key={snapshot.visit}>{children}</Fragment>
    </Navigation.Provider>
  );
}
export function NavigationPreferences({
  viewer,
  children,
}: {
  readonly viewer: SessionViewerResult;
  readonly children: ReactNode;
}): React.JSX.Element {
  const scope = navigationScope(viewer);
  return (
    <NavigationSession key={scope} scope={scope}>
      {children}
    </NavigationSession>
  );
}

/** Only fixed screen names are remembered. Records, forms and tokens stay transient. */
export function useRememberedSection<T extends string>(
  name: string,
  fallback: T,
  allowed: readonly T[],
  preferFallback = false,
): readonly [T, Dispatch<SetStateAction<T>>] {
  const context = useContext(Navigation);
  const key = context === null ? null : `tosho.navigation.v1:${context.scope}:${name}`;
  const snapshot = useSyncExternalStore(
    context?.history.subscribe ?? emptySubscribe,
    context?.history.getSnapshot ?? getEmptySnapshot,
    getEmptySnapshot,
  );
  const [local, setLocal] = useState<T>(() => {
    if (key && !preferFallback) {
      const remembered = snapshot.sections[name] as T | undefined;
      if (remembered !== undefined && allowed.includes(remembered)) return remembered;
      try {
        const saved = window.sessionStorage.getItem(key);
        if (saved && allowed.includes(saved as T)) return saved as T;
      } catch {
        /* Storage is optional. */
      }
    }
    return fallback;
  });
  const initialized = useRef(false);
  const saved = snapshot.sections[name] as T | undefined;
  const section =
    preferFallback && !initialized.current
      ? local
      : saved !== undefined && allowed.includes(saved)
        ? saved
        : local;
  useEffect(() => {
    initialized.current = true;
    context?.history.initialize(name, local, allowed, preferFallback);
    // Registration belongs to this mount, not to each newly allocated allowlist.
  }, [context, name]);
  useEffect(() => {
    if (!key) return;
    try {
      window.sessionStorage.setItem(key, section);
    } catch {
      /* Storage is optional. */
    }
  }, [key, section]);
  const setSection: Dispatch<SetStateAction<T>> = (action) => {
    const current = context?.history.getSnapshot().sections[name] as T | undefined;
    const previous = current !== undefined && allowed.includes(current) ? current : section;
    const next = typeof action === 'function' ? action(previous) : action;
    if (!allowed.includes(next)) return;
    if (context) context.history.change(name, next);
    else setLocal(next);
  };
  return [section, setSection];
}
